import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useCart } from "../lib/cart";
import api, { BACKEND_URL } from "../lib/api";
import { useSEO } from "../lib/seo";
import BlurHashImage from "../components/BlurHashImage";

const SAVED_CHECKOUT_KEY = "printmytees_checkout_details_v1";
const SAVED_CHECKOUT_REMEMBER_KEY = "printmytees_checkout_remember_v1";

const checkoutImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) return url;
  return `${BACKEND_URL}${url.startsWith("/") ? "" : "/"}${url}`;
};

const PAY_OPTIONS = [
  "UPI",
  "Credit/Debit Card",
  "Net Banking",
  "Wallets",
  "Cash on Delivery",
];

const STATES = [
  "Tamil Nadu",
  "Kerala",
  "Karnataka",
  "Andhra Pradesh",
  "Telangana",
  "Puducherry",
  "Maharashtra",
  "Delhi",
  "Gujarat",
  "Rajasthan",
  "West Bengal",
  "Uttar Pradesh",
  "Other",
];

export default function Checkout() {
  const { items, clear } = useCart();
  const nav = useNavigate();

  const [pay, setPay] = useState(PAY_OPTIONS[0]);
  const [codEnabled, setCodEnabled] = useState(false);
  const [razorpayEnabled, setRazorpayEnabled] = useState(false);
  const paymentOptions = PAY_OPTIONS.filter((option) =>
    option === "Cash on Delivery" ? codEnabled : razorpayEnabled
  );
  const [busy, setBusy] = useState(false);
  const [taxQuote, setTaxQuote] = useState(null);
  const [taxQuoteLoading, setTaxQuoteLoading] = useState(false);
  const [taxQuoteError, setTaxQuoteError] = useState("");
  const cartTotal = items.reduce(
    (sum, item) =>
      sum +
      Number(
        item.total_price ??
          (item.price || 0) * (item.quantity || 1)
      ),
    0
  );

  const productDeliveryCharge = items.reduce(
    (highest, item) => Math.max(highest, Number(item.delivery_charge || 0)),
    0
  );
  const hasGift = items.some((item) => item.product_type === "gift");
  const sharedDeliveryCharge = productDeliveryCharge > 0 ? productDeliveryCharge : (hasGift ? 50 : 0);
  const shippingCharge = cartTotal >= 1000 ? 0 : sharedDeliveryCharge;

  const offerDiscount = Number(taxQuote?.discount || 0);
  const gstAmount = Number(taxQuote?.gst_amount || 0);
  // The server quote is authoritative for shipping, discounts, tax, and
  // the final payable amount. This keeps gifts and apparel consistent with
  // the amount validated when the order is created.
  const quotedShipping = Number(taxQuote?.shipping_charge ?? shippingCharge);
  const grandTotal = Number(
    taxQuote?.grand_total ??
      Math.max(0, cartTotal - offerDiscount) + quotedShipping + gstAmount
  );

  const formatPaymentAmount = (value) => {
    const amount = Number(value || 0);
    return amount.toLocaleString("en-IN", {
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2,
    });
  };

  useSEO({
    title: "Checkout — PrintMyTees",
    description:
      "Complete your custom apparel order.",
  });

  const [form, setForm] = useState({
    full_name: "",
    mobile: "",
    email: "",
    address: "",
    city: "",
    district: "",
    state: "Tamil Nadu",
    pincode: "",
    notes: "",
  });

  const [rememberDetails, setRememberDetails] = useState(true);
  const [savedDetailsLoaded, setSavedDetailsLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    api.get("/payment-options")
      .then(({ data }) => {
        if (!active) return;
        const codAvailable = data?.cod_enabled === true;
        const onlineAvailable = data?.razorpay_enabled === true;
        setCodEnabled(codAvailable);
        setRazorpayEnabled(onlineAvailable);
        if (!onlineAvailable) setPay(codAvailable ? "Cash on Delivery" : "");
      })
      .catch(() => {
        // Fail closed when payment settings cannot be loaded.
        if (active) {
          setCodEnabled(false);
          setRazorpayEnabled(false);
          setPay("");
        }
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!codEnabled && pay === "Cash on Delivery") {
      setPay(razorpayEnabled ? PAY_OPTIONS[0] : "");
    } else if (!razorpayEnabled && pay !== "Cash on Delivery" && codEnabled) {
      setPay("Cash on Delivery");
    }
  }, [codEnabled, razorpayEnabled, pay]);

  useEffect(() => {
    try {
      const savedPreference = localStorage.getItem(
        SAVED_CHECKOUT_REMEMBER_KEY
      );
      const shouldRemember = savedPreference !== "false";
      setRememberDetails(shouldRemember);

      if (shouldRemember) {
        const saved = localStorage.getItem(SAVED_CHECKOUT_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          setForm((current) => ({
            ...current,
            full_name: parsed.full_name || "",
            mobile: parsed.mobile || "",
            email: parsed.email || "",
            address: parsed.address || "",
            city: parsed.city || "",
            district: parsed.district || "",
            state: parsed.state || "Tamil Nadu",
            pincode: parsed.pincode || "",
          }));
        }
      }
    } catch {
      // Ignore invalid local saved details.
    } finally {
      setSavedDetailsLoaded(true);
    }
  }, []);

  const clearSavedDetails = () => {
    localStorage.removeItem(SAVED_CHECKOUT_KEY);
    localStorage.setItem(SAVED_CHECKOUT_REMEMBER_KEY, "false");
    setRememberDetails(false);
    setForm((current) => ({
      ...current,
      full_name: "",
      mobile: "",
      email: "",
      address: "",
      city: "",
      district: "",
      state: "Tamil Nadu",
      pincode: "",
    }));
    toast.success("Saved checkout details cleared from this device.");
  };

  useEffect(() => {
    if (!savedDetailsLoaded) return;

    try {
      localStorage.setItem(
        SAVED_CHECKOUT_REMEMBER_KEY,
        rememberDetails ? "true" : "false"
      );

      if (!rememberDetails) {
        localStorage.removeItem(SAVED_CHECKOUT_KEY);
        return;
      }

      const details = {
        full_name: form.full_name,
        mobile: form.mobile,
        email: form.email,
        address: form.address,
        city: form.city,
        district: form.district,
        state: form.state,
        pincode: form.pincode,
      };

      const hasDetails = Object.values(details).some(
        (value) => String(value || "").trim()
      );

      if (hasDetails) {
        localStorage.setItem(
          SAVED_CHECKOUT_KEY,
          JSON.stringify(details)
        );
      }
    } catch {
      // Local saving is optional.
    }
  }, [
    savedDetailsLoaded,
    rememberDetails,
    form.full_name,
    form.mobile,
    form.email,
    form.address,
    form.city,
    form.district,
    form.state,
    form.pincode,
  ]);

  useEffect(() => {
    let cancelled = false;

    const loadTaxQuote = async () => {
      if (!items.length) {
        setTaxQuote(null);
        setTaxQuoteLoading(false);
        setTaxQuoteError("");
        return;
      }

      try {
        const res = await api.post("/tax-quote", {
          state: form.state,
          mobile: form.mobile,
          email: form.email || null,
          items,
        });

        if (!cancelled) {
          setTaxQuote(res.data);
          setTaxQuoteError("");
        }
      } catch {
        if (!cancelled) {
          setTaxQuote(null);
          setTaxQuoteError(
            "Unable to refresh the order total. Please check your details and try again."
          );
        }
      } finally {
        if (!cancelled) {
          setTaxQuoteLoading(false);
        }
      }
    };

    // Mark the quote stale immediately, before the debounce. Otherwise the
    // previous quote can remain submittable for 400 ms after checkout details change.
    setTaxQuoteLoading(true);
    setTaxQuoteError("");
    setTaxQuote(null);

    const timer = window.setTimeout(loadTaxQuote, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [items, form.state, form.email, form.mobile]);

  const set = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const validate = () => {
    if (!form.full_name.trim()) {
      return "Enter your full name";
    }

    if (!/^[6-9]\d{9}$/.test(
      form.mobile.replace(/\s+/g, "")
    )) {
      return "Enter a valid 10-digit Indian mobile number";
    }

    if (!form.address.trim()) {
      return "Enter your delivery address";
    }

    if (!form.city.trim()) {
      return "Enter your city";
    }

    if (!form.district.trim()) {
      return "Enter your district";
    }

    if (!form.state.trim()) {
      return "Select your state";
    }

    if (!/^\d{6}$/.test(form.pincode)) {
      return "Enter a valid 6-digit PIN code";
    }

    return null;
  };

  const submit = async (e) => {
    e.preventDefault();

    if (items.length === 0) {
      return toast.error("Your cart is empty");
    }

    if (!paymentOptions.includes(pay)) {
      return toast.error("No payment method is currently available. Please contact PrintMyTees.");
    }

    const err = validate();

    if (err) {
      return toast.error(err);
    }

    if (taxQuoteLoading) {
      return toast.error("Please wait while your order total is refreshed.");
    }

    if (taxQuoteError || !taxQuote) {
      return toast.error(
        taxQuoteError ||
          "Your order total could not be verified. Please refresh the total and try again."
      );
    }

    if (pay !== "Cash on Delivery" && !window.Razorpay) {
      return toast.error(
        "Payment gateway is not loaded. Please refresh the page."
      );
    }

    setBusy(true);

    try {
      if (pay === "Cash on Delivery") {
        const res = await api.post("/payments/cod-order", {
          ...form,
          payment_method: "Cash on Delivery",
          items,
        });
        const { order_number } = res.data;

        if (rememberDetails) {
          try {
            localStorage.setItem(SAVED_CHECKOUT_KEY, JSON.stringify({
              full_name: form.full_name,
              mobile: form.mobile,
              email: form.email,
              address: form.address,
              city: form.city,
              district: form.district,
              state: form.state,
              pincode: form.pincode,
            }));
          } catch {
            // Local saving is optional.
          }
        } else {
          localStorage.removeItem(SAVED_CHECKOUT_KEY);
        }

        sessionStorage.setItem(
          "printmytees_order_confirmation_v1",
          JSON.stringify({ order_number, mobile: form.mobile })
        );
        clear();
        toast.success("Cash on Delivery order placed successfully.");
        nav(`/order/${order_number}`);
        return;
      }

      // 1. Create Razorpay order on backend
      const res = await api.post(
        "/payments/create-order",
        {
          ...form,
          payment_method: pay,
          items,
        }
      );

      const {
        order_number,
        razorpay_order_id,
        amount_paise,
        currency,
        key_id,
      } = res.data;

      // 2. Open Razorpay Checkout
      const options = {
        key: key_id,
        amount: amount_paise,
        currency,
        name: "PrintMyTees",
        description: `Order ${order_number}`,
        order_id: razorpay_order_id,

        prefill: {
          name: form.full_name,
          email: form.email,
          contact: form.mobile,
        },

        notes: {
          order_number,
        },

        theme: {
          color: "#D95328",
        },

        handler: async function (response) {
          try {
            // 3. Verify payment securely
            const verifyRes = await api.post(
              "/payments/verify",
              {
                ...form,
                payment_method: pay,
                items,
                order_number,
                razorpay_payment_id:
                  response.razorpay_payment_id,
                razorpay_order_id:
                  response.razorpay_order_id,
                razorpay_signature:
                  response.razorpay_signature,
              }
            );

            if (verifyRes.data?.success) {
              if (rememberDetails) {
                try {
                  localStorage.setItem(
                    SAVED_CHECKOUT_KEY,
                    JSON.stringify({
                      full_name: form.full_name,
                      mobile: form.mobile,
                      email: form.email,
                      address: form.address,
                      city: form.city,
                      district: form.district,
                      state: form.state,
                      pincode: form.pincode,
                    })
                  );
                } catch {
                  // Saving locally is optional; payment should continue normally.
                }
              } else {
                localStorage.removeItem(SAVED_CHECKOUT_KEY);
              }

              // 4. Keep only the confirmation lookup details for this browser session.
              // The backend still requires the matching mobile number.
              sessionStorage.setItem(
                "printmytees_order_confirmation_v1",
                JSON.stringify({
                  order_number,
                  mobile: form.mobile,
                })
              );

              // 5. Clear cart only after verified payment
              clear();

              // 6. Go to order confirmation
              nav(`/order/${order_number}`);
            } else {
              toast.error(
                "Payment verification failed."
              );
            }
          } catch (error) {
            console.error(
              "Razorpay verification error:",
              error?.response?.data || error
            );

            const detail =
              error?.response?.data?.detail ||
              "Payment was received, but verification failed. Please contact PrintMyTees.";

            toast.error(detail);
          } finally {
            setBusy(false);
          }
        },

        modal: {
          ondismiss: function () {
            setBusy(false);
            toast.error("Payment cancelled.");
          },
        },
      };

      const razorpay =
        new window.Razorpay(options);

      razorpay.on(
        "payment.failed",
        function () {
          setBusy(false);
          toast.error(
            "Payment failed. Please try again."
          );
        }
      );

      razorpay.open();
    } catch (error) {
      console.error(
        "Razorpay checkout error:",
        error
      );

      toast.error(
        error?.response?.data?.detail ||
          "Could not start payment. Please try again."
      );

      setBusy(false);
    }
  };

  return (
    <div
      className="container-pmt py-12"
      data-testid="checkout-page"
    >
      {/* =========================
          PAGE HEADER
      ========================== */}

      <div className="flex items-center justify-between gap-3">
        <div className="overline">
          Checkout
        </div>
        <button
          type="button"
          onClick={() => nav("/cart")}
          className="text-sm underline underline-offset-4 text-stone-600 hover:text-[#D95328]"
          data-testid="checkout-back-to-cart"
        >
          ← Back to cart
        </button>
      </div>

      <h1 className="display-h2 mt-2">
        Review & confirm.
      </h1>

      <p className="mt-2 text-sm text-stone-500">
        Please fill in your details and place your
        order.
      </p>

      <div className="mt-5 grid grid-cols-3 gap-2 max-w-2xl text-[10px] sm:text-xs uppercase tracking-wide">
        <div className="border border-[#E6DFD5] bg-white px-2 py-2 text-center text-stone-500">1. Cart</div>
        <div className="border border-[#D95328] bg-[#F3EDE2] px-2 py-2 text-center font-semibold text-[#D95328]">2. Details</div>
        <div className="border border-[#E6DFD5] bg-white px-2 py-2 text-center text-stone-500">3. Secure Payment</div>
      </div>

      <div className="mt-4 flex flex-col gap-3 rounded-xl border border-[#d9e4d8] bg-[#f5faf4] p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="checkout-help-panel">
        <div><p className="text-sm font-bold text-[#183b2a]">Need help before placing your order?</p><p className="mt-1 text-xs leading-5 text-[#526458]">Confirm sizes, personalisation or delivery details with us before you pay.</p></div>
        <a href="https://wa.me/919566853582" target="_blank" rel="noreferrer" className="inline-flex min-h-11 shrink-0 items-center justify-center rounded-full border border-[#b7cdb8] bg-white px-4 py-2 text-sm font-semibold text-[#183b2a] transition hover:bg-[#eaf4e9] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D95328]">WhatsApp support</a>
      </div>

      <form
        onSubmit={submit}
        className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-8 items-start"
      >
        {/* =====================================================
            LEFT COLUMN — CUSTOMER INFORMATION
        ====================================================== */}

        <div className="lg:col-span-2">
          <div className="card-pmt p-5">
            <div
              className="font-bold uppercase mb-4"
              style={{
                fontFamily:
                  "Barlow Condensed, sans-serif",
              }}
            >
              Customer Information
            </div>

            <div className="mb-4 flex items-center justify-between gap-3 rounded border border-[#E6DFD5] bg-[#FAF7F2] px-3 py-3">
              <label className="flex items-center gap-3 text-xs text-stone-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberDetails}
                  onChange={(e) => setRememberDetails(e.target.checked)}
                  className="sr-only peer"
                />
                <span
                  aria-hidden="true"
                  className="flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border-2 border-[#141413] bg-white text-white peer-checked:bg-[#D95328] peer-checked:border-[#D95328] peer-focus-visible:ring-2 peer-focus-visible:ring-[#D95328] peer-focus-visible:ring-offset-1"
                >
                  {rememberDetails && <span className="text-sm font-bold leading-none">✓</span>}
                </span>
                <span>
                  <span className="font-semibold text-stone-800">
                    Remember my details on this device
                  </span>
                  <span className="block text-[10px] text-stone-500 mt-0.5">
                    Saved locally on this device for faster checkout. Sent to PrintMyTees only when you place an order.
                  </span>
                </span>
              </label>

              <button
                type="button"
                onClick={clearSavedDetails}
                className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-red-600 hover:underline"
              >
                Clear
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Full Name */}
              <div>
                <div className="label-pmt">Full Name <span className="text-red-500">*</span></div>

                <input
                  required
                  autoComplete="name"
                  className="input-pmt"
                  placeholder="Enter your full name"
                  value={form.full_name}
                  onChange={(e) =>
                    set(
                      "full_name",
                      e.target.value
                    )
                  }
                  data-testid="checkout-name"
                />
              </div>

              {/* Mobile */}
              <div>
                <div className="label-pmt">Mobile <span className="text-red-500">*</span></div>

                <input
                  required
                  inputMode="numeric"
                  autoComplete="tel"
                  className="input-pmt"
                  placeholder="Enter mobile number"
                  value={form.mobile}
                  onChange={(e) =>
                    set(
                      "mobile",
                      e.target.value.replace(
                        /\D/g,
                        ""
                      )
                    )
                  }
                  maxLength={10}
                  data-testid="checkout-mobile"
                />
              </div>

              {/* Email */}
              <div className="sm:col-span-2">
                <div className="label-pmt">Email <span className="text-red-500">*</span></div>

                <input
                  required
                  type="email"
                  autoComplete="email"
                  className="input-pmt"
                  placeholder="Enter your email address"
                  value={form.email}
                  onChange={(e) =>
                    set(
                      "email",
                      e.target.value
                    )
                  }
                  data-testid="checkout-email"
                />
              </div>

              {/* Address */}
              <div className="sm:col-span-2">
                <div className="label-pmt">Delivery Address <span className="text-red-500">*</span></div>

                <textarea
                  required
                  rows={2}
                  className="input-pmt"
                  placeholder="House no, Street, Area, Landmark"
                  value={form.address}
                  onChange={(e) =>
                    set(
                      "address",
                      e.target.value
                    )
                  }
                  data-testid="checkout-address"
                />
              </div>

              {/* City */}
              <div>
                <div className="label-pmt">City <span className="text-red-500">*</span></div>

                <input
                  required
                  className="input-pmt"
                  placeholder="City"
                  value={form.city}
                  onChange={(e) =>
                    set(
                      "city",
                      e.target.value
                    )
                  }
                  data-testid="checkout-city"
                />
              </div>

              {/* District */}
              <div>
                <div className="label-pmt">District <span className="text-red-500">*</span></div>

                <input
                  required
                  className="input-pmt"
                  placeholder="District"
                  value={form.district}
                  onChange={(e) =>
                    set(
                      "district",
                      e.target.value
                    )
                  }
                  data-testid="checkout-district"
                />
              </div>

              {/* State */}
              <div>
                <div className="label-pmt">State <span className="text-red-500">*</span></div>

                <select
                  required
                  className="input-pmt"
                  value={form.state}
                  onChange={(e) =>
                    set(
                      "state",
                      e.target.value
                    )
                  }
                  data-testid="checkout-state"
                >
                  {STATES.map((state) => (
                    <option
                      key={state}
                      value={state}
                    >
                      {state}
                    </option>
                  ))}
                </select>
              </div>

              {/* PIN */}
              <div>
                <div className="label-pmt">PIN Code <span className="text-red-500">*</span></div>

                <input
                  required
                  inputMode="numeric"
                  autoComplete="postal-code"
                  pattern="\d{6}"
                  maxLength={6}
                  className="input-pmt"
                  placeholder="PIN code"
                  value={form.pincode}
                  onChange={(e) =>
                    set(
                      "pincode",
                      e.target.value.replace(
                        /\D/g,
                        ""
                      )
                    )
                  }
                  data-testid="checkout-pincode"
                />
              </div>

              {/* Notes */}
              <div className="sm:col-span-2">
                <div className="label-pmt">
                  Order Notes
                  <span className="text-stone-400 ml-1">
                    (Optional)
                  </span>
                </div>

                <textarea
                  rows={2}
                  className="input-pmt"
                  placeholder="Any special instructions?"
                  value={form.notes}
                  onChange={(e) =>
                    set(
                      "notes",
                      e.target.value
                    )
                  }
                  data-testid="checkout-notes"
                />
              </div>
            </div>
          </div>
        </div>

        {/* =====================================================
            RIGHT COLUMN
        ====================================================== */}

        <div className="lg:col-span-3 space-y-5">
          {/* =================================================
              PAYMENT METHOD
          ================================================== */}

          <div
            className="card-pmt p-5"
            data-testid="checkout-payment"
          >
            <div
              className="font-bold uppercase mb-2"
              style={{
                fontFamily:
                  "Barlow Condensed, sans-serif",
              }}
            >
              Payment Method
            </div>

            <p className="text-sm text-stone-500 mb-4">
              {razorpayEnabled && codEnabled
                ? "Choose online payment with Razorpay or pay in cash when your order is delivered."
                : razorpayEnabled
                  ? "Choose an available online payment method powered by Razorpay."
                  : codEnabled
                    ? "Online payment is temporarily unavailable. You can pay in cash when your order is delivered."
                    : "Payments are temporarily unavailable. Please contact PrintMyTees before placing an order."}
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {paymentOptions.map((option) => (
                <button
                  type="button"
                  key={option}
                  aria-pressed={pay === option}
                  onClick={() =>
                    setPay(option)
                  }
                  data-testid={`checkout-pay-${option
                    .replace(
                      /[^a-z0-9]+/gi,
                      "-"
                    )
                    .toLowerCase()}`}
                  className={`px-3 py-3 text-xs font-semibold uppercase border transition-colors ${
                    pay === option
                      ? "bg-[#141413] text-[#FAF7F2] border-[#141413]"
                      : "bg-white border-[#E6DFD5] hover:border-[#141413]"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>

            <div
              className="mt-4 p-3 bg-[#F3EDE2] border border-[#E6DFD5] text-xs text-stone-700"
              data-testid="checkout-payment-note"
            >
              {pay === "Cash on Delivery"
                ? "Pay in cash when your order is delivered. Your order will remain payment-pending until collection."
                : "🔒 Secure online payment powered by Razorpay. Your order will be confirmed automatically after successful payment."}
            </div>
          </div>

          {/* =================================================
              ORDER SUMMARY
          ================================================== */}

          <aside
            className="card-pmt p-7 h-fit"
            data-testid="checkout-summary"
          >
            {/* Summary Header */}
            <div className="flex items-center justify-between">
              <div className="overline">
                Order Summary
              </div>

              <div className="text-xs uppercase text-stone-500">
                {items.length}{" "}
                {items.length === 1
                  ? "Item"
                  : "Items"}
              </div>
            </div>

            {/* Cart Items */}
            <div className="mt-5 space-y-4 pr-1">
              {items.map((item) => {
                const quantity =
                  item.quantity || 1;

                const unitPrice = Number(
                  item.price || 0
                );

                const totalPrice = Number(
                  item.total_price ??
                    unitPrice * quantity
                );

                return (
                  <div
                    key={item._id}
                    className="border-b border-[#E6DFD5] pb-4"
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-3 sm:gap-4">
                      {/* Item details */}
                      <div className="min-w-0 flex-1">
                        <div className="flex gap-3 items-start">
                          {item.image && (
                            <div className="w-16 h-16 shrink-0 overflow-hidden border border-[#E6DFD5] bg-[#F3EDE2]">
                              <img
                                src={checkoutImageUrl(item.image)}
                                alt={item.product_name || "Product"}
                                className="w-full h-full object-cover"
                                width={64}
                                height={64}
                              />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                        <div className="font-semibold text-base break-words">
                              {item.product_name}
                            </div>

                        {item.design_id && (
                              <div className="text-xs text-stone-500 mt-1 break-words">
                                Design ID:{" "}
                                {item.design_id}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 mt-3">
                          {item.product_type !== "gift" && item.size && (
                            <span className="px-2.5 py-1 bg-[#F3EDE2] text-xs">
                              Size:{" "}
                              {item.size}
                            </span>
                          )}

                          {item.product_type !== "gift" && item.color && (
                            <span className="px-2.5 py-1 bg-[#F3EDE2] text-xs">
                              Colour:{" "}
                              {item.color}
                            </span>
                          )}

                          {item.product_type !== "gift" && item.quality && (
                            <span className="px-2.5 py-1 bg-[#F3EDE2] text-xs">
                              Quality:{" "}
                              {item.quality}
                            </span>
                          )}

                          <span className="px-2.5 py-1 bg-[#F3EDE2] text-xs">
                            Qty: {quantity}
                          </span>
                        </div>

                        {/* Shop customisation details */}
                        {(item.custom_text ||
                          item.custom_name ||
                          item.custom_number) && (
                          <div className="mt-3 p-3 bg-[#F8F5F0] border border-[#E6DFD5] text-xs">
                            <div className="font-semibold text-stone-800 mb-2">
                              Customisation
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-stone-600">
                              {item.custom_name && (
                                <div><span className="font-semibold text-stone-700">Name:</span> {item.custom_name}</div>
                              )}
                              {item.custom_number && (
                                <div><span className="font-semibold text-stone-700">Number:</span> {item.custom_number}</div>
                              )}
                              {item.custom_text && (
                                <div className="sm:col-span-2"><span className="font-semibold text-stone-700">Text:</span> {item.custom_text}</div>
                              )}
                            </div>
                          </div>
                        )}

                        {item.gift_options && Object.values(item.gift_options).some((value) => String(value || "").trim()) && (
                          <div className="mt-3 p-3 bg-[#F8F5F0] border border-[#E6DFD5] text-xs" data-testid="checkout-gift-details">
                            <div className="font-semibold text-stone-800 mb-2">Gift Personalisation</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-stone-600">
                              {Object.entries(item.gift_options).filter(([, value]) => String(value || "").trim()).map(([label, value]) => (
                                <div key={label} className="break-words"><span className="font-semibold text-stone-700">{item.gift_option_labels?.[label] || label.replace(/_/g, " ")}:</span> {value}</div>
                              ))}
                            </div>
                          </div>
                        )}

                        {item.notes && (
                          <div className="text-xs text-stone-500 mt-2">
                            Notes:{" "}
                            {item.notes}
                          </div>
                        )}
                      </div>

                      {/* Price */}
                      <div className="text-right flex-shrink-0">
                        <div className="font-mono text-sm">
                          ₹{formatPaymentAmount(unitPrice)}
                        </div>

                        <div className="text-[11px] text-stone-500">
                          each
                        </div>

                        <div className="font-semibold mt-2">
                          ₹{formatPaymentAmount(totalPrice)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {items.length === 0 && (
                <div className="text-sm text-stone-500">
                  <div>Your cart is empty.</div>
                  <div className="mt-2 flex flex-wrap gap-4">
                    <Link
                      to="/shop"
                      className="underline font-semibold"
                    >
                      Browse Shop
                    </Link>
                    <Link
                      to="/designs"
                      className="underline font-semibold"
                    >
                      Browse Designs
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Price Breakdown */}
            <div className="mt-5 space-y-3">
              <div className="flex justify-between text-sm">
                <span>
                  Product Subtotal ({items.length}{" "}
                  {items.length === 1
                    ? "item"
                    : "items"})
                </span>

                <span className="font-mono">
                  ₹{formatPaymentAmount(cartTotal)}
                </span>
              </div>

              {offerDiscount > 0 && (
                <div className="flex justify-between text-sm text-green-700">
                  <span>Offer Discount</span>
                  <span className="font-mono">-₹{formatPaymentAmount(offerDiscount)}</span>
                </div>
              )}

              <div className="flex justify-between text-sm">
                <span>
                  Shipping
                </span>

                <span className="font-semibold text-green-600">
                  {taxQuote?.offer_free_delivery || quotedShipping <= 0
                    ? "FREE"
                    : `₹${formatPaymentAmount(quotedShipping)}`}
                </span>
              </div>

              {taxQuote?.gst_enabled && (
                <>
                  <div className="flex justify-between text-sm">
                    <span>GST</span>
                    <span className="font-mono">
                      ₹{gstAmount.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>

                  {Number(taxQuote.cgst_amount || 0) > 0 && (
                    <div className="flex justify-between text-xs text-stone-500">
                      <span>CGST + SGST</span>
                      <span>
                        ₹{Number(taxQuote.cgst_amount).toFixed(2)} + ₹{Number(taxQuote.sgst_amount).toFixed(2)}
                      </span>
                    </div>
                  )}

                  {Number(taxQuote.igst_amount || 0) > 0 && (
                    <div className="flex justify-between text-xs text-stone-500">
                      <span>IGST</span>
                      <span>₹{Number(taxQuote.igst_amount).toFixed(2)}</span>
                    </div>
                  )}
                </>
              )}

              {/* Total */}
              <div className="mt-4 p-4 bg-[#F3EDE2] border border-[#E6DFD5]">
                <div className="flex justify-between items-center">
                  <span className="font-semibold">
                    Grand Total
                  </span>

                  <span className="font-mono text-xl font-bold text-[#D95328]">
                    ₹{formatPaymentAmount(grandTotal)}
                  </span>
                </div>
              </div>
            </div>

            {/* Pay Now */}
            <button
              type="submit"
              disabled={
                busy ||
                items.length === 0 ||
                taxQuoteLoading ||
                Boolean(taxQuoteError) ||
                !taxQuote
              }
              aria-busy={busy}
              className="btn-primary w-full mt-5 py-4 disabled:opacity-60"
              data-testid="checkout-place-order"
            >
              {busy
                ? (pay === "Cash on Delivery" ? "Placing Order…" : "Opening Payment…")
                : (pay === "Cash on Delivery" ? "Place COD Order" : "Pay Now")}
            </button>

            {busy && (
              <div
                className="mt-2 text-center text-xs text-stone-500"
                role="status"
              >
                {pay === "Cash on Delivery" ? "Submitting your order…" : "Connecting to secure Razorpay checkout…"}
              </div>
            )}

            {/* Security */}
            <div className="mt-4 text-center text-xs text-stone-500">
              🔒 Your information is secure with us.
            </div>
          </aside>
        </div>
      </form>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-stone-500">
        <div className="border border-[#E6DFD5] bg-white p-3">🔒 Payment is processed securely by Razorpay.</div>
        <div className="border border-[#E6DFD5] bg-white p-3">💬 Need help? Contact PrintMyTees on WhatsApp.</div>
        <div className="border border-[#E6DFD5] bg-white p-3">📦 Your order is confirmed after successful payment.</div>
      </div>
    </div>
  );
}